import React, { useState, useEffect } from 'react';
import { Form, FormField, FormVariable } from '@/firebase/formService';
import { ComplianceTemplate, complianceService, ReferenceDocument, uploadReferenceDocument } from '@/firebase/complianceService';
import { locationService, Location } from '@/firebase/locationService';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useAuth } from '@/contexts/AuthContext';
import { useNotification } from '@/contexts/NotificationContext';
import { X, Plus, ChevronRight, ChevronLeft, CheckCircle2, Circle, Search, HelpCircle, FileText, Image, Upload, Sparkles } from 'lucide-react';
import ComplianceForm from './ComplianceForm';
import FormulaExamplesModal from './FormulaExamplesModal';
import FormulaTestModal from './FormulaTestModal';
import { extractTableFormItems, isGeminiConfigured, identifyItemColumn } from '@/services/geminiService';
import * as XLSX from 'xlsx';

interface FormQuickSetupWizardProps {
  onComplete: (formId: string) => void;
  onCancel: () => void;
  initialData?: {
    name?: string;
    description?: string;
    fields?: FormField[];
    templateId?: string;
  };
}

type WizardStep = 'form' | 'template' | 'locations';

interface WizardData {
  form: {
    name: string;
    description: string;
    layoutType: 'standard' | 'table';
    fields: FormField[];
    tableFields?: FormField[];
    itemsDefinition?: { type: 'static'; items: string[] };
  };
  templateId: string | null;
  template: ComplianceTemplate | null;
  locationIds: string[];
  isLocationSpecific: boolean;
  referenceDocuments: ReferenceDocument[];
}

const FormQuickSetupWizard: React.FC<FormQuickSetupWizardProps> = ({ onComplete, onCancel, initialData }) => {
  const { currentOrganization } = useOrganization();
  const { user } = useAuth();
  const { showNotification } = useNotification();
  
  // Determine initial step: if templateId is provided, start at template step, otherwise form
  const getInitialStep = (): WizardStep => {
    if (initialData?.templateId) {
      return 'template';
    }
    return 'form';
  };
  
  const [currentStep, setCurrentStep] = useState<WizardStep>(getInitialStep());
  const [templates, setTemplates] = useState<ComplianceTemplate[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [isCreatingTemplate, setIsCreatingTemplate] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [locationSearchTerm, setLocationSearchTerm] = useState('');
  const [templateSearchTerm, setTemplateSearchTerm] = useState('');
  const [categories, setCategories] = useState<string[]>([]);
  
  const [wizardData, setWizardData] = useState<WizardData>({
    form: {
      name: initialData?.name || '',
      description: initialData?.description || '',
      layoutType: 'standard',
      fields: initialData?.fields || [],
      tableFields: [],
      itemsDefinition: undefined
    },
    templateId: initialData?.templateId || null,
    template: null,
    locationIds: [],
    isLocationSpecific: false,
    referenceDocuments: []
  });

  // Field management state
  const [newFieldLabel, setNewFieldLabel] = useState('');
  const [newFieldType, setNewFieldType] = useState<FormField['type']>('text');
  const [newTableFieldLabel, setNewTableFieldLabel] = useState('');
  const [newTableFieldType, setNewTableFieldType] = useState<FormField['type']>('number');
  const [newItemName, setNewItemName] = useState('');
  const [showLayoutHelp, setShowLayoutHelp] = useState(false);
  const [showFormulaExamples, setShowFormulaExamples] = useState<number | string | null>(null);
  const [showFormulaTest, setShowFormulaTest] = useState<number | string | null>(null);
  const [formulaErrors, setFormulaErrors] = useState<Record<number | string, string>>({});
  const [uploadingReference, setUploadingReference] = useState(false);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  // AI extraction state
  const [isExtractingItems, setIsExtractingItems] = useState(false);
  const [extractedItems, setExtractedItems] = useState<string[]>([]);
  const [extractionNotes, setExtractionNotes] = useState<string>('');
  const [showExtractedItems, setShowExtractedItems] = useState(false);
  
  // Excel/CSV upload state
  const [uploadedFileData, setUploadedFileData] = useState<{
    headers: string[];
    rows: any[][];
    itemColumnIndex: number;
    itemColumnName: string;
  } | null>(null);
  const [isProcessingFile, setIsProcessingFile] = useState(false);
  const [columnMappings, setColumnMappings] = useState<Record<number, { fieldLabel: string; fieldType: FormField['type'] }>>({});

  const fieldTypes: FormField['type'][] = ['text', 'number', 'date', 'email', 'tel', 'textarea', 'select', 'checkbox', 'radio', 'photo', 'reference', 'calculated'];

  useEffect(() => {
    loadTemplates();
    loadLocations();
  }, [currentOrganization?.id]);

  const loadTemplates = async () => {
    if (!currentOrganization?.id) return;
    setIsLoadingTemplates(true);
    try {
      const result = await complianceService.getAllTemplates(currentOrganization.id);
      if (result.success && result.data) {
        setTemplates(result.data);
        const templateCategories = Array.from(new Set(result.data.map(t => t.category).filter(Boolean)));
        setCategories(templateCategories);
      }
    } catch (error) {
      console.error('Error loading templates:', error);
    } finally {
      setIsLoadingTemplates(false);
    }
  };

  useEffect(() => {
    if (initialData?.templateId && templates.length > 0) {
      const template = templates.find(t => t.id === initialData.templateId);
      if (template) {
        setWizardData(prev => ({
          ...prev,
          template: template,
          templateId: template.id || null
        }));
      }
    }
  }, [initialData?.templateId, templates]);

  const loadLocations = async () => {
    if (!currentOrganization?.id) return;
    
    let userLocationIds: string[] | undefined;
    let userRole: string | undefined;
    if (user?.id) {
      try {
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('@/firebase/config');
        const userRef = doc(db, 'users', user.id);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const userData = userSnap.data();
          userLocationIds = userData.locationIds;
          userRole = userData.role;
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    }
    
    try {
      const result = await locationService.getAccessibleLocations(
        currentOrganization.id,
        user?.id,
        userRole,
        userLocationIds
      );
      if (result.success && result.data) {
        setLocations(result.data);
      }
    } catch (error) {
      console.error('Error loading locations:', error);
    }
  };

  const steps: Array<{ id: WizardStep; label: string; description: string }> = [
    { id: 'form', label: 'Form', description: 'Create your form' },
    { id: 'template', label: 'Link Compliance', description: 'Link compliance template' },
    { id: 'locations', label: 'Reference Documents', description: 'Add reference documents (optional)' }
  ];

  const currentStepIndex = steps.findIndex(s => s.id === currentStep);
  const isLastStep = currentStepIndex === steps.length - 1;
  const isFirstStep = currentStepIndex === 0;

  // Field management functions
  const addField = () => {
    if (!newFieldLabel.trim()) return;
    const newField: FormField = {
      label: newFieldLabel.trim(),
      type: newFieldType,
      order: wizardData.form.fields.length + 1,
      required: false,
      ...(newFieldType === 'select' || newFieldType === 'radio' || newFieldType === 'checkbox' ? { options: [] } : {})
    };
    setWizardData({
      ...wizardData,
      form: {
        ...wizardData.form,
        fields: [...wizardData.form.fields, newField]
      }
    });
    setNewFieldLabel('');
  };

  const removeField = (index: number) => {
    const updatedFields = wizardData.form.fields.filter((_, i) => i !== index);
    updatedFields.forEach((field, i) => {
      field.order = i + 1;
    });
    setWizardData({
      ...wizardData,
      form: {
        ...wizardData.form,
        fields: updatedFields
      }
    });
  };

  const addTableField = () => {
    if (!newTableFieldLabel.trim()) return;
    const newField: FormField = {
      label: newTableFieldLabel.trim(),
      type: newTableFieldType,
      order: (wizardData.form.tableFields || []).length + 1,
      required: false,
      ...(newTableFieldType === 'select' || newTableFieldType === 'radio' || newTableFieldType === 'checkbox' ? { options: [] } : {})
    };
    setWizardData({
      ...wizardData,
      form: {
        ...wizardData.form,
        tableFields: [...(wizardData.form.tableFields || []), newField]
      }
    });
    setNewTableFieldLabel('');
  };

  const removeTableField = (index: number) => {
    const updatedFields = (wizardData.form.tableFields || []).filter((_, i) => i !== index);
    updatedFields.forEach((field, i) => {
      field.order = i + 1;
    });
    setWizardData({
      ...wizardData,
      form: {
        ...wizardData.form,
        tableFields: updatedFields
      }
    });
  };

  const addTableItem = () => {
    if (!newItemName.trim()) return;
    const currentItems = wizardData.form.itemsDefinition?.items || [];
    setWizardData({
      ...wizardData,
      form: {
        ...wizardData.form,
        itemsDefinition: {
          type: 'static',
          items: [...currentItems, newItemName.trim()]
        }
      }
    });
    setNewItemName('');
  };

  const removeTableItem = (index: number) => {
    const currentItems = wizardData.form.itemsDefinition?.items || [];
    const updatedItems = currentItems.filter((_, i) => i !== index);
    setWizardData({
      ...wizardData,
      form: {
        ...wizardData.form,
        itemsDefinition: updatedItems.length > 0 ? {
          type: 'static',
          items: updatedItems
        } : undefined
      }
    });
  };

  const handleExtractItemsFromFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    
    // Validate file type
    const isPDF = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    const isWord = file.type === 'application/msword' ||
      file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      file.name.toLowerCase().endsWith('.doc') ||
      file.name.toLowerCase().endsWith('.docx');
    const isImage = file.type.startsWith('image/');

    if (!isPDF && !isWord && !isImage) {
      showNotification('Unsupported file type. Please upload PDF, Word, or image files.', 'error');
      event.target.value = '';
      return;
    }

    if (!isGeminiConfigured()) {
      showNotification('AI service is not configured. Please contact your administrator.', 'error');
      event.target.value = '';
      return;
    }

    setIsExtractingItems(true);
    setExtractedItems([]);
    setExtractionNotes('');

    try {
      const result = await extractTableFormItems(file);
      
      if (result.items && result.items.length > 0) {
        setExtractedItems(result.items);
        setExtractionNotes(result.confidenceNotes || '');
        setShowExtractedItems(true);
      } else {
        showNotification('No items were found in the document. Please try a different file or add items manually.', 'info');
      }
    } catch (error: any) {
      console.error('Error extracting items:', error);
      showNotification(`Failed to extract items: ${error.message || 'Unknown error'}`, 'error');
    } finally {
      setIsExtractingItems(false);
      event.target.value = '';
    }
  };

  const handleUseExtractedItems = () => {
    if (extractedItems.length === 0) return;
    
    const currentItems = wizardData.form.itemsDefinition?.items || [];
    // Merge extracted items with existing items, avoiding duplicates
    const mergedItems = [...currentItems];
    extractedItems.forEach(item => {
      if (!mergedItems.includes(item)) {
        mergedItems.push(item);
      }
    });
    
    setWizardData({
      ...wizardData,
      form: {
        ...wizardData.form,
        itemsDefinition: {
          type: 'static',
          items: mergedItems
        }
      }
    });
    
    // Reset extraction state
    setExtractedItems([]);
    setExtractionNotes('');
    setShowExtractedItems(false);
    showNotification(`Added ${extractedItems.length} item${extractedItems.length !== 1 ? 's' : ''} to the list`, 'success');
  };

  const handleDiscardExtractedItems = () => {
    setExtractedItems([]);
    setExtractionNotes('');
    setShowExtractedItems(false);
  };

  const handleExcelCsvUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    const isExcel = file.name.toLowerCase().endsWith('.xlsx') || file.name.toLowerCase().endsWith('.xls');
    const isCsv = file.name.toLowerCase().endsWith('.csv');

    if (!isExcel && !isCsv) {
      showNotification('Please upload an Excel (.xlsx, .xls) or CSV (.csv) file', 'error');
      event.target.value = '';
      return;
    }

    setIsProcessingFile(true);
    setUploadedFileData(null);
    setColumnMappings({});

    try {
      let headers: string[] = [];
      let rows: any[][] = [];

      if (isCsv) {
        // Parse CSV
        const text = await file.text();
        const lines = text.split('\n').filter(line => line.trim());
        if (lines.length < 2) {
          throw new Error('CSV must have at least a header row and one data row');
        }

        // Simple CSV parsing (handles quoted values)
        const parseCSVLine = (line: string): string[] => {
          const result: string[] = [];
          let current = '';
          let inQuotes = false;
          for (let i = 0; i < line.length; i++) {
            const char = line[i];
            if (char === '"') {
              if (inQuotes && line[i + 1] === '"') {
                // Escaped quote
                current += '"';
                i++; // Skip next quote
              } else {
                inQuotes = !inQuotes;
              }
            } else if (char === ',' && !inQuotes) {
              result.push(current.trim());
              current = '';
            } else {
              current += char;
            }
          }
          result.push(current.trim());
          return result;
        };

        headers = parseCSVLine(lines[0]);
        for (let i = 1; i < lines.length; i++) {
          const values = parseCSVLine(lines[i]);
          if (values.length > 0 && values.some(v => v.trim())) {
            rows.push(values);
          }
        }
      } else {
        // Parse Excel
        const arrayBuffer = await file.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const data = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' }) as any[][];
        
        if (data.length < 2) {
          throw new Error('Excel file must have at least a header row and one data row');
        }

        headers = data[0].map(h => String(h || '').trim()).filter(h => h);
        for (let i = 1; i < data.length; i++) {
          const row = data[i];
          if (row && row.some(cell => cell !== '' && cell !== null && cell !== undefined)) {
            rows.push(row.map(cell => cell === null || cell === undefined ? '' : String(cell)));
          }
        }
      }

      if (headers.length === 0) {
        throw new Error('No headers found in file');
      }

      if (rows.length === 0) {
        throw new Error('No data rows found in file');
      }

      // Use AI to identify Item column
      let itemColumnIndex = 0;
      let itemColumnName = headers[0] || 'Item';
      
      if (isGeminiConfigured()) {
        try {
          const identification = await identifyItemColumn(headers);
          itemColumnIndex = identification.itemColumnIndex;
          itemColumnName = identification.itemColumnName;
        } catch (error) {
          console.error('Error identifying item column:', error);
          // Fallback: find column with "item" in name
          const fallbackIndex = headers.findIndex(h => 
            h.toLowerCase().includes('item') || 
            h.toLowerCase().includes('product') || 
            h.toLowerCase() === 'name'
          );
          if (fallbackIndex >= 0) {
            itemColumnIndex = fallbackIndex;
            itemColumnName = headers[fallbackIndex];
          }
        }
      } else {
        // Fallback: find column with "item" in name
        const fallbackIndex = headers.findIndex(h => 
          h.toLowerCase().includes('item') || 
          h.toLowerCase().includes('product') || 
          h.toLowerCase() === 'name'
        );
        if (fallbackIndex >= 0) {
          itemColumnIndex = fallbackIndex;
          itemColumnName = headers[fallbackIndex];
        }
      }

      // Extract items from the identified column
      const items = rows
        .map(row => row[itemColumnIndex]?.toString().trim())
        .filter(item => item && item.length > 0)
        .filter((item, index, self) => self.indexOf(item) === index); // Remove duplicates

      if (items.length === 0) {
        throw new Error('No items found in the identified Item column');
      }

      setUploadedFileData({
        headers,
        rows,
        itemColumnIndex,
        itemColumnName
      });

      // Auto-populate items
      setWizardData({
        ...wizardData,
        form: {
          ...wizardData.form,
          layoutType: 'table', // Force table layout for Excel/CSV uploads
          itemsDefinition: {
            type: 'static',
            items
          }
        }
      });

      showNotification(`File uploaded successfully! Found ${items.length} items and ${headers.length} columns.`, 'success');
    } catch (error: any) {
      console.error('Error processing file:', error);
      showNotification(`Failed to process file: ${error.message || 'Unknown error'}`, 'error');
    } finally {
      setIsProcessingFile(false);
      event.target.value = '';
    }
  };

  const handleApplyColumnMappings = () => {
    if (!uploadedFileData) return;

    const newTableFields: FormField[] = [];
    const itemReferenceValues: Record<string, Record<string, string | number>> = {};

    // Create reference fields for mapped columns
    Object.entries(columnMappings).forEach(([colIndexStr, mapping]) => {
      const colIndex = parseInt(colIndexStr);
      if (colIndex === uploadedFileData.itemColumnIndex) return; // Skip item column

      const field: FormField = {
        label: mapping.fieldLabel,
        type: mapping.fieldType,
        order: newTableFields.length + 1,
        required: false
      };

      // If it's a reference field, populate values per item
      if (mapping.fieldType === 'reference') {
        const referenceValues: Record<string, string | number> = {};
        uploadedFileData.rows.forEach((row, rowIndex) => {
          const itemName = row[uploadedFileData.itemColumnIndex]?.toString().trim();
          if (itemName) {
            const cellValue = row[colIndex]?.toString().trim() || '';
            if (cellValue) {
              // Try to parse as number, otherwise keep as string
              const numValue = parseFloat(cellValue);
              referenceValues[itemName] = isNaN(numValue) ? cellValue : numValue;
            }
          }
        });
        field.itemReferenceValues = referenceValues;
      }

      newTableFields.push(field);
    });

    // Merge with existing table fields
    const existingFields = wizardData.form.tableFields || [];
    const mergedFields = [...existingFields, ...newTableFields].map((field, index) => ({
      ...field,
      order: index + 1
    }));

    setWizardData({
      ...wizardData,
      form: {
        ...wizardData.form,
        tableFields: mergedFields
      }
    });

    showNotification(`Added ${newTableFields.length} field(s) from column mappings`, 'success');
    setUploadedFileData(null);
    setColumnMappings({});
  };

  const updateField = (index: number, updates: Partial<FormField>) => {
    const updatedFields = [...wizardData.form.fields];
    updatedFields[index] = { ...updatedFields[index], ...updates };
    setWizardData({
      ...wizardData,
      form: {
        ...wizardData.form,
        fields: updatedFields
      }
    });
  };

  const updateTableField = (index: number, updates: Partial<FormField>) => {
    if (!wizardData.form.tableFields) return;
    const updatedFields = [...wizardData.form.tableFields];
    updatedFields[index] = { ...updatedFields[index], ...updates };
    setWizardData({
      ...wizardData,
      form: {
        ...wizardData.form,
        tableFields: updatedFields
      }
    });
  };

  const validateFormula = (fieldIndex: number, formula: string) => {
    if (!formula || !wizardData.form.fields) {
      setFormulaErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldIndex];
        return newErrors;
      });
      return;
    }

    const errors: string[] = [];
    const field = wizardData.form.fields[fieldIndex];
    
    if (!field) return;

    // Check if formula references fields that don't exist or are after this field
    const fieldLabels = wizardData.form.fields
      .filter(f => f.label && f.type !== 'calculated' && f.order < (field.order || 0))
      .map(f => f.label);
    
    // Simple validation: check for common syntax issues
    const openParens = (formula.match(/\(/g) || []).length;
    const closeParens = (formula.match(/\)/g) || []).length;
    if (openParens !== closeParens) {
      errors.push('Mismatched parentheses');
    }

    // Check for unclosed quotes
    const quoteCount = (formula.match(/"/g) || []).length;
    if (quoteCount % 2 !== 0) {
      errors.push('Unclosed quotes in formula');
    }

    if (errors.length > 0) {
      setFormulaErrors(prev => ({ ...prev, [fieldIndex]: errors.join(', ') }));
    } else {
      setFormulaErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldIndex];
        return newErrors;
      });
    }
  };

  const validateTableFormula = (fieldIndex: number, formula: string) => {
    if (!formula || !wizardData.form.tableFields) {
      setFormulaErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`table-${fieldIndex}`];
        return newErrors;
      });
      return;
    }

    const errors: string[] = [];
    const field = wizardData.form.tableFields[fieldIndex];
    
    if (!field) return;

    // Check if formula references fields that don't exist or are after this field
    const fieldLabels = wizardData.form.tableFields
      .filter(f => f.label && f.type !== 'calculated' && f.order < (field.order || 0))
      .map(f => f.label);
    
    // Simple validation: check for common syntax issues
    const openParens = (formula.match(/\(/g) || []).length;
    const closeParens = (formula.match(/\)/g) || []).length;
    if (openParens !== closeParens) {
      errors.push('Mismatched parentheses');
    }

    // Check for unclosed quotes
    const quoteCount = (formula.match(/"/g) || []).length;
    if (quoteCount % 2 !== 0) {
      errors.push('Unclosed quotes in formula');
    }

    if (errors.length > 0) {
      setFormulaErrors(prev => ({ ...prev, [`table-${fieldIndex}`]: errors.join(', ') }));
    } else {
      setFormulaErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`table-${fieldIndex}`];
        return newErrors;
      });
    }
  };

  const handleLocationToggle = (locationId: string) => {
    setWizardData(prev => {
      const currentIds = prev.locationIds || [];
      const newIds = currentIds.includes(locationId)
        ? currentIds.filter(id => id !== locationId)
        : [...currentIds, locationId];
      return { ...prev, locationIds: newIds };
    });
  };

  const handleLocationSpecificToggle = (checked: boolean) => {
    setWizardData(prev => ({
      ...prev,
      isLocationSpecific: checked,
      locationIds: checked ? prev.locationIds : []
    }));
  };

  const filteredLocations = locations.filter(location =>
    location.name.toLowerCase().includes(locationSearchTerm.toLowerCase())
  );

  const filteredTemplates = templates.filter(template => {
    if (!templateSearchTerm) return true;
    const search = templateSearchTerm.toLowerCase();
    return (
      template.name.toLowerCase().includes(search) ||
      template.category?.toLowerCase().includes(search)
    );
  });

  const handleTemplateSelect = (templateId: string) => {
    const template = templates.find(t => t.id === templateId);
    setWizardData({
      ...wizardData,
      templateId,
      template: template || null
    });
  };

  const handleTemplateCreated = async (templateData: Partial<ComplianceTemplate>) => {
    if (!currentOrganization?.id) {
      showNotification('Organization is required', 'error');
      return;
    }

    try {
      const userInfo = user ? { id: user.id, name: user.name || 'Unknown', email: user.email || 'Unknown' } : undefined;
      const result = await complianceService.addTemplate(
        templateData as Omit<ComplianceTemplate, 'id'>,
        currentOrganization.id,
        userInfo
      );

      if (result.success && result.id) {
        // Reload all templates to get the latest list
        await loadTemplates();
        
        // Get the newly created template
        const getResult = await complianceService.getTemplate(result.id);
        if (getResult.success && getResult.data) {
          const newTemplate = getResult.data;
          
          if (newTemplate.category && !categories.includes(newTemplate.category)) {
            setCategories([...categories, newTemplate.category]);
          }
          
          setWizardData({
            ...wizardData,
            templateId: newTemplate.id || null,
            template: newTemplate
          });
          setIsCreatingTemplate(false);
          showNotification('Compliance template created successfully', 'success');
        } else {
          showNotification('Template created but could not load details', 'info');
          setIsCreatingTemplate(false);
        }
      } else {
        showNotification(`Error creating template: ${result.error || 'Unknown error'}`, 'error');
      }
    } catch (error: any) {
      console.error('Error creating template:', error);
      showNotification(`Error: ${error.message || 'Failed to create template'}`, 'error');
    }
  };

  const handleNext = () => {
    // Validate current step
    if (currentStep === 'form') {
      if (!wizardData.form.name.trim()) {
        showNotification('Please enter a form name', 'error');
        return;
      }
      if (wizardData.form.layoutType === 'standard' && wizardData.form.fields.length === 0) {
        showNotification('Please add at least one form field', 'error');
        return;
      }
      if (wizardData.form.layoutType === 'table') {
        if (!wizardData.form.itemsDefinition?.items || wizardData.form.itemsDefinition.items.length === 0) {
          showNotification('Please add at least one item for table layout', 'error');
          return;
        }
        if (!wizardData.form.tableFields || wizardData.form.tableFields.length === 0) {
          showNotification('Please add at least one table field', 'error');
          return;
        }
      }
      if (wizardData.isLocationSpecific && wizardData.locationIds.length === 0) {
        showNotification('Please select at least one location or disable location restriction', 'error');
        return;
      }
    } else if (currentStep === 'template') {
      if (!wizardData.templateId) {
        showNotification('Please select or create a compliance template', 'error');
        return;
      }
    }

    // Move to next step
    if (isLastStep) {
      handleComplete();
    } else {
      const nextStepIndex = currentStepIndex + 1;
      setCurrentStep(steps[nextStepIndex].id);
    }
  };

  const handleBack = () => {
    if (!isFirstStep) {
      const prevStepIndex = currentStepIndex - 1;
      setCurrentStep(steps[prevStepIndex].id);
    }
  };

  const handleComplete = async () => {
    if (!currentOrganization?.id) {
      showNotification('Organization is required', 'error');
      return;
    }

    if (!wizardData.form.name.trim() || !wizardData.templateId) {
      showNotification('Please complete all required fields', 'error');
      return;
    }

    setIsSaving(true);
    try {
      // Ensure templateId is a string, not null
      if (!wizardData.templateId) {
        showNotification('Please select or create a compliance template', 'error');
        setIsSaving(false);
        return;
      }

      // Clean up formData - ensure fields don't have undefined properties (same as FormForm)
      const cleanedFormData: Partial<Form> = {
        name: wizardData.form.name,
        templateId: wizardData.templateId, // Now guaranteed to be a string
        layoutType: wizardData.form.layoutType || 'standard',
        ...(wizardData.form.description && { description: wizardData.form.description }),
        // Standard form fields
        ...(wizardData.form.layoutType === 'standard' && {
          fields: wizardData.form.fields?.map(field => {
            const cleanedField: FormField = {
              label: field.label,
              type: field.type,
              order: field.order,
              ...(field.required !== undefined && { required: field.required }),
              ...(field.placeholder && { placeholder: field.placeholder }),
              // Include options array for select/radio/checkbox types (even if empty)
              ...((field.type === 'select' || field.type === 'radio' || field.type === 'checkbox') && 
                  { options: field.options || [] }),
              ...(field.defaultValue && { defaultValue: field.defaultValue }),
              // Include formula for calculated type
              ...(field.type === 'calculated' && field.formula && { 
                formula: field.formula 
              }),
              ...(field.validation && { validation: field.validation })
            };
            return cleanedField;
          }),
        }),
        // Table form fields
        ...(wizardData.form.layoutType === 'table' && {
          itemsDefinition: wizardData.form.itemsDefinition ? {
            type: 'static' as const,
            items: wizardData.form.itemsDefinition.items || []
          } : undefined,
          tableFields: wizardData.form.tableFields?.map(field => {
            const cleanedField: FormField = {
              label: field.label,
              type: field.type,
              order: field.order,
              ...(field.required !== undefined && { required: field.required }),
              ...(field.placeholder && { placeholder: field.placeholder }),
              ...((field.type === 'select' || field.type === 'radio' || field.type === 'checkbox') && 
                  { options: field.options || [] }),
              ...(field.defaultValue && { defaultValue: field.defaultValue }),
              ...(field.type === 'reference' && field.itemReferenceValues && { 
                itemReferenceValues: field.itemReferenceValues 
              }),
              ...(field.type === 'calculated' && field.formula && { 
                formula: field.formula 
              }),
              ...(field.validation && { validation: field.validation })
            };
            return cleanedField;
          }) || []
        }),
        // Always include variables array (even if empty) so Firestore can properly update it
        variables: [],
        // Set locationIds to undefined if toggle is disabled (backward compatible), otherwise use selected locationIds
        locationIds: wizardData.isLocationSpecific && wizardData.locationIds.length > 0
          ? wizardData.locationIds
          : undefined,
        // Include reference documents if any
        ...(wizardData.referenceDocuments && wizardData.referenceDocuments.length > 0 && { 
          referenceDocuments: wizardData.referenceDocuments 
        })
      };

      const { formService } = await import('@/firebase/formService');
      const formResult = await formService.addForm(
        cleanedFormData as Omit<Form, 'id'>,
        currentOrganization.id,
        user ? { id: user.id, name: user.name || 'Unknown', email: user.email || 'Unknown' } : undefined
      );

      if (formResult.success && formResult.id) {
        showNotification('Form created successfully!', 'success');
        onComplete(formResult.id);
      } else {
        showNotification(`Error creating form: ${formResult.error || 'Unknown error'}`, 'error');
      }
    } catch (error: any) {
      console.error('Error completing wizard:', error);
      showNotification(`Error: ${error.message || 'Failed to create form'}`, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const renderProgressIndicator = () => {
    return (
      <div className="flex items-center justify-between px-6 py-4 bg-gray-50 border-b border-gray-200">
        {steps.map((step, index) => {
          const isCompleted = index < currentStepIndex;
          const isCurrent = index === currentStepIndex;
          const stepNum = index + 1;

          return (
            <React.Fragment key={step.id}>
              <div className="flex flex-col items-center flex-1">
                <div className="flex items-center gap-2 mb-1">
                  {isCompleted ? (
                    <CheckCircle2 className="w-6 h-6 text-primary-600" />
                  ) : isCurrent ? (
                    <Circle className="w-6 h-6 text-primary-600 fill-primary-600" />
                  ) : (
                    <Circle className="w-6 h-6 text-gray-400" />
                  )}
                  <span className={`text-sm font-medium ${isCurrent ? 'text-primary-600' : isCompleted ? 'text-gray-700' : 'text-gray-400'}`}>
                    Step {stepNum}
                  </span>
                </div>
                <span className={`text-xs ${isCurrent ? 'text-gray-700' : 'text-gray-500'}`}>
                  {step.label}
                </span>
              </div>
              {index < steps.length - 1 && (
                <div className={`flex-1 h-0.5 mx-2 ${isCompleted ? 'bg-primary-600' : 'bg-gray-300'}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  const renderFormStep = () => {
    return (
      <div className="space-y-6 p-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Create Your Form</h3>
          <p className="text-sm text-gray-600">Start by creating your form with fields that need to be filled.</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Form Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={wizardData.form.name}
            onChange={(e) => setWizardData({
              ...wizardData,
              form: { ...wizardData.form, name: e.target.value }
            })}
            placeholder="e.g., Daily Inventory Count"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description (Optional)
          </label>
          <textarea
            value={wizardData.form.description}
            onChange={(e) => setWizardData({
              ...wizardData,
              form: { ...wizardData.form, description: e.target.value }
            })}
            placeholder="Brief description of this form..."
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
            Layout Type <span className="text-red-500">*</span>
            <button
              type="button"
              onClick={() => setShowLayoutHelp(!showLayoutHelp)}
              className="text-gray-400 hover:text-primary-600 transition-colors"
              title="Click for help"
            >
              <HelpCircle className="w-4 h-4" />
            </button>
          </label>
          <select
            value={wizardData.form.layoutType}
            onChange={(e) => {
              const newLayoutType = e.target.value as 'standard' | 'table';
              setWizardData({
                ...wizardData,
                form: {
                  ...wizardData.form,
                  layoutType: newLayoutType,
                  ...(newLayoutType === 'standard' && {
                    tableFields: [],
                    itemsDefinition: undefined
                  }),
                  ...(newLayoutType === 'table' && {
                    fields: []
                  })
                }
              });
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="standard">Standard (Vertical Fields)</option>
            <option value="table">Table/Grid (Items as Rows)</option>
          </select>
          
          {/* Layout Type Help Modal */}
          {showLayoutHelp && (
            <div className="mt-2 bg-blue-50 border border-blue-200 rounded-md p-3">
              <div className="flex items-start gap-2">
                <HelpCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1 text-xs text-blue-900">
                  <p className="font-semibold mb-2">Layout Type Guide</p>
                  <div className="space-y-2">
                    <div>
                      <p className="font-medium text-blue-800 mb-1">Standard Layout (Vertical Fields):</p>
                      <ul className="list-disc ml-4 space-y-0.5 text-blue-700">
                        <li>Fields are displayed vertically, one after another</li>
                        <li>Each field takes full width of the form</li>
                        <li>Best for: Single-subject forms, surveys, checklists, general data collection</li>
                        <li>Each form submission captures one set of values</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-medium text-blue-800 mb-1">Table/Grid Layout (Items as Rows):</p>
                      <ul className="list-disc ml-4 space-y-0.5 text-blue-700">
                        <li>Items are displayed as rows in a table</li>
                        <li>Fields become columns that apply to each item</li>
                        <li>Best for: Inventory tracking, item-based data collection, comparing multiple items</li>
                        <li>Each form submission captures values for multiple items at once</li>
                        <li>Perfect for par level inventory: items (Wine, Liquor) as rows, fields (Current Inventory, Par Level, Status) as columns</li>
                      </ul>
                    </div>
                    <div className="mt-2 pt-2 border-t border-blue-300">
                      <p className="text-blue-800 font-medium">Example Use Cases:</p>
                      <p className="text-blue-700 mt-1">
                        <strong>Standard:</strong> Employee onboarding form, incident report, maintenance request
                      </p>
                      <p className="text-blue-700">
                        <strong>Table:</strong> Daily inventory count, product quality check across items, temperature monitoring for multiple locations
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowLayoutHelp(false)}
                    className="mt-2 text-blue-600 hover:text-blue-800 underline text-xs"
                  >
                    Close help
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {/* Layout Type Info Banner (shows when table is selected) */}
          {wizardData.form.layoutType === 'table' && !showLayoutHelp && (
            <div className="mt-2 bg-blue-50 border border-blue-200 rounded-md p-3">
              <p className="text-xs text-blue-800">
                <strong>Table Layout Selected:</strong> Items will be rows, fields will be columns. Perfect for inventory tracking with multiple items.
              </p>
            </div>
          )}
        </div>

        {wizardData.form.layoutType === 'standard' ? (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Form Fields <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={newFieldLabel}
                onChange={(e) => setNewFieldLabel(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    addField();
                  }
                }}
                placeholder="Enter field label"
                className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-lg"
              />
              <select
                value={newFieldType}
                onChange={(e) => setNewFieldType(e.target.value as FormField['type'])}
                className="px-2 py-1.5 text-sm border border-gray-300 rounded-lg min-w-[120px]"
              >
                {fieldTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={addField}
                className="btn-primary px-3 py-1.5 text-sm whitespace-nowrap flex items-center gap-1"
                disabled={!newFieldLabel.trim()}
              >
                <Plus className="w-4 h-4" /> Add
              </button>
            </div>
            {wizardData.form.fields.length > 0 ? (
              <div className="space-y-2 border border-gray-200 rounded-lg p-3">
                {wizardData.form.fields.map((field, index) => (
                  <div key={index} className="space-y-2 bg-gray-50 p-3 rounded border border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2 flex-1">
                        <span className="text-xs text-gray-500 w-6">{index + 1}.</span>
                        <input
                          type="text"
                          value={field.label}
                          onChange={(e) => updateField(index, { label: e.target.value })}
                          className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded"
                          placeholder="Field label"
                        />
                        <select
                          value={field.type}
                          onChange={(e) => {
                            const newType = e.target.value as FormField['type'];
                            const updates: Partial<FormField> = { type: newType };
                            if (newType === 'select' || newType === 'radio' || newType === 'checkbox') {
                              updates.options = field.options || [];
                            } else {
                              updates.options = undefined;
                            }
                            if (newType !== 'calculated') {
                              updates.formula = undefined;
                            }
                            updateField(index, updates);
                          }}
                          className="px-2 py-1 text-sm border border-gray-300 rounded"
                        >
                          {fieldTypes.map(type => (
                            <option key={type} value={type}>{type}</option>
                          ))}
                        </select>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeField(index)}
                        className="text-red-600 hover:text-red-800 text-sm px-2"
                      >
                        ×
                      </button>
                    </div>
                    
                    {/* Reference field - show warning for standard forms */}
                    {field.type === 'reference' && (
                      <div className="bg-orange-50 border border-orange-200 rounded-md p-3">
                        <div className="flex items-start gap-2">
                          <HelpCircle className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" />
                          <div className="flex-1 text-xs text-orange-900">
                            <p className="font-medium mb-1">Reference Field</p>
                            <p className="text-orange-800">
                              Reference fields are only supported in <strong>table forms</strong>. They display fixed values that are read-only when the form is filled out. For standard forms, please use a different field type or switch to table layout.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Calculated field formula input */}
                    {field.type === 'calculated' && (
                      <div className="space-y-2">
                        {/* Inline Help Panel */}
                        <div className="bg-blue-50 border border-blue-200 rounded-md p-2">
                          <div className="flex items-start gap-2">
                            <HelpCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                            <div className="flex-1 text-xs">
                              <p className="font-medium text-blue-900 mb-1">How to Write Formulas</p>
                              <ul className="list-disc ml-4 space-y-0.5 text-blue-800 mb-2">
                                <li>Reference fields by their exact label (e.g., "Current Inventory")</li>
                                <li>Use operators: +, -, *, /, &lt;, &gt;, ==</li>
                                <li>Use functions: IF, MAX, MIN, ABS</li>
                                <li>Text values must be in quotes: "Below Par"</li>
                              </ul>
                              <button 
                                type="button"
                                onClick={() => setShowFormulaExamples(index)}
                                className="text-blue-600 hover:text-blue-800 underline text-xs font-medium"
                              >
                                View Examples & Full Guide →
                              </button>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-1 mb-1">
                          <div className="text-xs text-gray-600">Formula (Required):</div>
                        </div>
                        
                        {/* Show available field labels */}
                        {wizardData.form.fields && wizardData.form.fields.filter(f => f.label && f.type !== 'calculated' && f.order < (field.order || 0)).length > 0 && (
                          <div className="mb-1">
                            <div className="text-xs text-gray-500 mb-1">Available fields:</div>
                            <div className="flex flex-wrap gap-1">
                              {wizardData.form.fields
                                .filter(f => f.label && f.type !== 'calculated' && f.order < (field.order || 0))
                                .map(f => (
                                  <button
                                    key={f.label}
                                    type="button"
                                    onClick={() => {
                                      const currentFormula = field.formula || '';
                                      const cursorPos = (document.activeElement as HTMLTextAreaElement)?.selectionStart || currentFormula.length;
                                      const newFormula = currentFormula.slice(0, cursorPos) + f.label + currentFormula.slice(cursorPos);
                                      updateField(index, { formula: newFormula });
                                      setTimeout(() => {
                                        const textarea = document.querySelector(`textarea[data-field-index="${index}"]`) as HTMLTextAreaElement;
                                        if (textarea) {
                                          textarea.focus();
                                          const newPos = cursorPos + f.label.length;
                                          textarea.setSelectionRange(newPos, newPos);
                                        }
                                      }, 0);
                                    }}
                                    className="text-xs px-1.5 py-0.5 bg-gray-100 hover:bg-gray-200 rounded border border-gray-300 cursor-pointer"
                                    title="Click to insert into formula"
                                  >
                                    "{f.label}"
                                  </button>
                                ))}
                            </div>
                          </div>
                        )}
                        
                        <textarea
                          data-field-index={index}
                          value={field.formula || ''}
                          onChange={(e) => {
                            updateField(index, { formula: e.target.value });
                            validateFormula(index, e.target.value);
                          }}
                          placeholder='Example: IF(Current Inventory < Par Level, "Below Par", "OK")'
                          className={`w-full px-2 py-1 text-xs border rounded font-mono ${
                            formulaErrors[index] ? 'border-red-300 bg-red-50' : 'border-gray-300'
                          }`}
                          rows={3}
                          required
                        />
                        
                        {/* Formula Validation Messages */}
                        {formulaErrors[index] && (
                          <p className="text-xs text-red-600 mt-0.5">
                            ⚠️ {formulaErrors[index]}
                          </p>
                        )}
                        {!formulaErrors[index] && field.formula && (
                          <div className="flex items-center gap-2 mt-0.5">
                            <p className="text-xs text-green-600">
                              ✓ Formula looks good
                            </p>
                            <button
                              type="button"
                              onClick={() => setShowFormulaTest(index)}
                              className="text-xs text-blue-600 hover:text-blue-800 underline"
                            >
                              🧪 Test with Sample Values
                            </button>
                          </div>
                        )}
                        
                        {!formulaErrors[index] && !field.formula && (
                          <p className="text-xs text-gray-500 mt-0.5">
                            Reference fields by their exact label. Supports: +, -, *, /, IF, MAX, MIN, &lt;, &gt;, ==
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 italic">No fields yet. Add fields manually above.</p>
            )}
          </div>
        ) : (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Items (Rows) <span className="text-red-500">*</span>
              </label>
              <div className="space-y-2">
                {/* Excel/CSV Upload Option */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="w-4 h-4 text-green-600" />
                    <span className="text-sm font-medium text-green-900">Upload Excel/CSV File</span>
                  </div>
                  <p className="text-xs text-green-800 mb-2">
                    Upload an Excel (.xlsx, .xls) or CSV file. AI will identify the Item column automatically. Other columns can be mapped to reference fields.
                  </p>
                  <label className="flex items-center justify-center w-full px-3 py-2 border-2 border-dashed border-green-300 rounded-lg cursor-pointer transition-colors hover:border-green-400 hover:bg-green-100">
                    {isProcessingFile ? (
                      <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-green-600 animate-spin" />
                        <span className="text-sm text-green-700">Processing file...</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Upload className="w-4 h-4 text-green-600" />
                        <span className="text-sm text-green-700">Upload Excel/CSV File</span>
                      </div>
                    )}
                    <input
                      type="file"
                      className="hidden"
                      accept=".xlsx,.xls,.csv"
                      onChange={handleExcelCsvUpload}
                      disabled={isProcessingFile}
                    />
                  </label>
                </div>

                {/* Column Mapping UI */}
                {uploadedFileData && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-sm font-medium text-purple-900">Column Mapping</h4>
                        <p className="text-xs text-purple-700 mt-1">
                          Item column identified: <strong>{uploadedFileData.itemColumnName}</strong> ({wizardData.form.itemsDefinition?.items?.length || 0} items)
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setUploadedFileData(null);
                          setColumnMappings({});
                        }}
                        className="text-purple-700 hover:text-purple-900"
                        title="Close"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {uploadedFileData.headers.map((header, colIndex) => {
                        if (colIndex === uploadedFileData.itemColumnIndex) return null; // Skip item column
                        
                        const mapping = columnMappings[colIndex];
                        const isMapped = !!mapping;
                        
                        return (
                          <div key={colIndex} className="bg-white border border-purple-200 rounded p-2">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-xs font-medium text-gray-700 flex-1">{header}</span>
                              <label className="flex items-center gap-1 text-xs text-gray-600">
                                <input
                                  type="checkbox"
                                  checked={isMapped}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setColumnMappings({
                                        ...columnMappings,
                                        [colIndex]: {
                                          fieldLabel: header,
                                          fieldType: 'reference'
                                        }
                                      });
                                    } else {
                                      const newMappings = { ...columnMappings };
                                      delete newMappings[colIndex];
                                      setColumnMappings(newMappings);
                                    }
                                  }}
                                  className="w-3 h-3"
                                />
                                Map as Reference Field
                              </label>
                            </div>
                            
                            {isMapped && (
                              <div className="space-y-2 mt-2 pt-2 border-t border-purple-100">
                                <div>
                                  <label className="text-xs text-gray-600 mb-1 block">Field Label:</label>
                                  <input
                                    type="text"
                                    value={mapping.fieldLabel}
                                    onChange={(e) => {
                                      setColumnMappings({
                                        ...columnMappings,
                                        [colIndex]: {
                                          ...mapping,
                                          fieldLabel: e.target.value
                                        }
                                      });
                                    }}
                                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                                    placeholder="Enter field label"
                                  />
                                </div>
                                <div>
                                  <label className="text-xs text-gray-600 mb-1 block">Field Type:</label>
                                  <select
                                    value={mapping.fieldType}
                                    onChange={(e) => {
                                      setColumnMappings({
                                        ...columnMappings,
                                        [colIndex]: {
                                          ...mapping,
                                          fieldType: e.target.value as FormField['type']
                                        }
                                      });
                                    }}
                                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                                  >
                                    <option value="reference">Reference (read-only values)</option>
                                    <option value="text">Text</option>
                                    <option value="number">Number</option>
                                  </select>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    
                    <button
                      type="button"
                      onClick={handleApplyColumnMappings}
                      className="w-full px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
                      disabled={Object.keys(columnMappings).length === 0}
                    >
                      Apply Column Mappings ({Object.keys(columnMappings).length} field{Object.keys(columnMappings).length !== 1 ? 's' : ''})
                    </button>
                  </div>
                )}

                {/* AI Extraction Option */}
                {isGeminiConfigured() && !uploadedFileData && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles className="w-4 h-4 text-blue-600" />
                      <span className="text-sm font-medium text-blue-900">AI-Powered Item Extraction</span>
                    </div>
                    <p className="text-xs text-blue-800 mb-2">
                      Upload a document (PDF, Word, or image) and AI will automatically extract item names for you.
                    </p>
                    <label className="flex items-center justify-center w-full px-3 py-2 border-2 border-dashed border-blue-300 rounded-lg cursor-pointer transition-colors hover:border-blue-400 hover:bg-blue-100">
                      {isExtractingItems ? (
                        <div className="flex items-center gap-2">
                          <Sparkles className="w-4 h-4 text-blue-600 animate-spin" />
                          <span className="text-sm text-blue-700">Extracting items...</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <Upload className="w-4 h-4 text-blue-600" />
                          <span className="text-sm text-blue-700">Upload Document to Extract Items</span>
                        </div>
                      )}
                      <input
                        type="file"
                        className="hidden"
                        accept=".pdf,.doc,.docx,image/*"
                        onChange={handleExtractItemsFromFile}
                        disabled={isExtractingItems}
                      />
                    </label>
                  </div>
                )}

                {/* Extracted Items Preview */}
                {showExtractedItems && extractedItems.length > 0 && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-green-600" />
                        <span className="text-sm font-medium text-green-900">
                          Found {extractedItems.length} item{extractedItems.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={handleDiscardExtractedItems}
                        className="text-green-700 hover:text-green-900"
                        title="Discard"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    {extractionNotes && (
                      <p className="text-xs text-green-800 mb-2 italic">{extractionNotes}</p>
                    )}
                    <div className="max-h-32 overflow-y-auto border border-green-200 rounded bg-white p-2 mb-2">
                      <div className="flex flex-wrap gap-1">
                        {extractedItems.map((item, idx) => (
                          <span key={idx} className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded border border-green-300">
                            {item}
                          </span>
                        ))}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleUseExtractedItems}
                      className="w-full px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                    >
                      Add {extractedItems.length} Item{extractedItems.length !== 1 ? 's' : ''} to List
                    </button>
                  </div>
                )}

                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newItemName}
                    onChange={(e) => setNewItemName(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        addTableItem();
                      }
                    }}
                    placeholder="Enter item name (e.g., Wine)"
                    className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={addTableItem}
                    className="btn-primary px-3 py-1.5 text-sm whitespace-nowrap flex items-center gap-1"
                    disabled={!newItemName.trim()}
                  >
                    <Plus className="w-4 h-4" /> Add Item
                  </button>
                </div>
                {wizardData.form.itemsDefinition?.items && wizardData.form.itemsDefinition.items.length > 0 && (
                  <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                    {wizardData.form.itemsDefinition.items.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-1 px-2 py-1 bg-white border border-gray-300 rounded">
                        <span className="text-sm text-gray-700">{item}</span>
                        <button
                          type="button"
                          onClick={() => removeTableItem(idx)}
                          className="text-red-600 hover:text-red-800 text-sm"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Table Fields (Columns) <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={newTableFieldLabel}
                  onChange={(e) => setNewTableFieldLabel(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      addTableField();
                    }
                  }}
                  placeholder="Field label (e.g., Current Inventory)"
                  className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-lg"
                />
                <select
                  value={newTableFieldType}
                  onChange={(e) => setNewTableFieldType(e.target.value as FormField['type'])}
                  className="px-2 py-1.5 text-sm border border-gray-300 rounded-lg min-w-[120px]"
                >
                  {fieldTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={addTableField}
                  className="btn-primary px-3 py-1.5 text-sm whitespace-nowrap flex items-center gap-1"
                  disabled={!newTableFieldLabel.trim()}
                >
                  <Plus className="w-4 h-4" /> Add
                </button>
              </div>
              {wizardData.form.tableFields && wizardData.form.tableFields.length > 0 ? (
                <div className="space-y-2 border border-gray-200 rounded-lg p-3">
                  {wizardData.form.tableFields.map((field, index) => (
                    <div key={index} className="space-y-2 bg-gray-50 p-3 rounded border border-gray-200">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2 flex-1">
                          <span className="text-xs text-gray-500 w-6">{index + 1}.</span>
                          <input
                            type="text"
                            value={field.label}
                            onChange={(e) => updateTableField(index, { label: e.target.value })}
                            className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded"
                            placeholder="Field label"
                          />
                          <select
                            value={field.type}
                            onChange={(e) => {
                              const newType = e.target.value as FormField['type'];
                              const updates: Partial<FormField> = { type: newType };
                              if (newType === 'select' || newType === 'radio' || newType === 'checkbox') {
                                updates.options = field.options || [];
                              } else {
                                updates.options = undefined;
                              }
                              if (newType !== 'reference') {
                                updates.itemReferenceValues = undefined;
                              }
                              if (newType !== 'calculated') {
                                updates.formula = undefined;
                              }
                              updateTableField(index, updates);
                            }}
                            className="px-2 py-1 text-sm border border-gray-300 rounded"
                          >
                            {fieldTypes.map(type => (
                              <option key={type} value={type}>{type}</option>
                            ))}
                          </select>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeTableField(index)}
                          className="text-red-600 hover:text-red-800 text-sm px-2"
                        >
                          ×
                        </button>
                      </div>
                      
                      {/* Reference field value input - per item */}
                      {field.type === 'reference' && wizardData.form.itemsDefinition?.items && wizardData.form.itemsDefinition.items.length > 0 && (
                        <div className="space-y-2">
                          <div className="text-xs font-medium text-gray-600 mb-1">Reference Values (per item):</div>
                          <div className="border border-gray-300 rounded p-2 bg-white space-y-1.5">
                            {wizardData.form.itemsDefinition.items.map((item, itemIdx) => (
                              <div key={itemIdx} className="flex items-center gap-2">
                                <label className="text-xs text-gray-700 w-20 flex-shrink-0">{item}:</label>
                                <input
                                  type="text"
                                  value={field.itemReferenceValues?.[item] !== undefined ? String(field.itemReferenceValues[item]) : ''}
                                  onChange={(e) => {
                                    const inputValue = e.target.value.trim();
                                    const value = inputValue === '' ? undefined : (isNaN(Number(inputValue)) ? inputValue : Number(inputValue));
                                    const currentItemValues = field.itemReferenceValues || {};
                                    const newItemValues: Record<string, string | number> = {};
                                    Object.entries(currentItemValues).forEach(([key, val]) => {
                                      if (val !== undefined) {
                                        newItemValues[key] = val;
                                      }
                                    });
                                    if (value !== undefined) {
                                      newItemValues[item] = value;
                                    } else {
                                      delete newItemValues[item];
                                    }
                                    updateTableField(index, { 
                                      itemReferenceValues: newItemValues
                                    });
                                  }}
                                  placeholder="Enter value (text or number)"
                                  className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded"
                                  required
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Calculated field formula input */}
                      {field.type === 'calculated' && (
                        <div className="space-y-2">
                          {/* Inline Help Panel */}
                          <div className="bg-blue-50 border border-blue-200 rounded-md p-2">
                            <div className="flex items-start gap-2">
                              <HelpCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                              <div className="flex-1 text-xs">
                                <p className="font-medium text-blue-900 mb-1">How to Write Formulas</p>
                                <ul className="list-disc ml-4 space-y-0.5 text-blue-800 mb-2">
                                  <li>Reference fields by their exact label (e.g., "Current Inventory")</li>
                                  <li>Use operators: +, -, *, /, &lt;, &gt;, ==</li>
                                  <li>Use functions: IF, MAX, MIN, ABS</li>
                                  <li>Text values must be in quotes: "Below Par"</li>
                                </ul>
                                <button 
                                  type="button"
                                  onClick={() => setShowFormulaExamples(`table-${index}`)}
                                  className="text-blue-600 hover:text-blue-800 underline text-xs font-medium"
                                >
                                  View Examples & Full Guide →
                                </button>
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center gap-1 mb-1">
                            <div className="text-xs text-gray-600">Formula (Required):</div>
                          </div>
                          
                          {/* Show available table field labels */}
                          {wizardData.form.tableFields && wizardData.form.tableFields.filter(f => f.label && f.type !== 'calculated' && f.order < (field.order || 0)).length > 0 && (
                            <div className="mb-1">
                              <div className="text-xs text-gray-500 mb-1">Available fields:</div>
                              <div className="flex flex-wrap gap-1">
                                {wizardData.form.tableFields
                                  .filter(f => f.label && f.type !== 'calculated' && f.order < (field.order || 0))
                                  .map(f => (
                                    <button
                                      key={f.label}
                                      type="button"
                                      onClick={() => {
                                        const currentFormula = field.formula || '';
                                        const cursorPos = (document.activeElement as HTMLTextAreaElement)?.selectionStart || currentFormula.length;
                                        const newFormula = currentFormula.slice(0, cursorPos) + f.label + currentFormula.slice(cursorPos);
                                        updateTableField(index, { formula: newFormula });
                                        setTimeout(() => {
                                          const textarea = document.querySelector(`textarea[data-table-field-index="${index}"]`) as HTMLTextAreaElement;
                                          if (textarea) {
                                            textarea.focus();
                                            const newPos = cursorPos + f.label.length;
                                            textarea.setSelectionRange(newPos, newPos);
                                          }
                                        }, 0);
                                      }}
                                      className="text-xs px-1.5 py-0.5 bg-gray-100 hover:bg-gray-200 rounded border border-gray-300 cursor-pointer"
                                      title="Click to insert into formula"
                                    >
                                      "{f.label}"
                                    </button>
                                  ))}
                              </div>
                            </div>
                          )}
                          
                          <textarea
                            data-table-field-index={index}
                            value={field.formula || ''}
                            onChange={(e) => {
                              updateTableField(index, { formula: e.target.value });
                              validateTableFormula(index, e.target.value);
                            }}
                            placeholder='Example: IF(Current Inventory < Par Level, "Below Par", "OK")'
                            className={`w-full px-2 py-1 text-xs border rounded font-mono ${
                              formulaErrors[`table-${index}`] ? 'border-red-300 bg-red-50' : 'border-gray-300'
                            }`}
                            rows={3}
                            required
                          />
                          
                          {/* Formula Validation Messages */}
                          {formulaErrors[`table-${index}`] && (
                            <p className="text-xs text-red-600 mt-0.5">
                              ⚠️ {formulaErrors[`table-${index}`]}
                            </p>
                          )}
                          {!formulaErrors[`table-${index}`] && field.formula && (
                            <div className="flex items-center gap-2 mt-0.5">
                              <p className="text-xs text-green-600">
                                ✓ Formula looks good
                              </p>
                              <button
                                type="button"
                                onClick={() => setShowFormulaTest(`table-${index}`)}
                                className="text-xs text-blue-600 hover:text-blue-800 underline"
                              >
                                🧪 Test with Sample Values
                              </button>
                            </div>
                          )}
                          
                          {!formulaErrors[`table-${index}`] && !field.formula && (
                            <p className="text-xs text-gray-500 mt-0.5">
                              Reference fields by their exact label. Supports: +, -, *, /, IF, MAX, MIN, &lt;, &gt;, ==
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 italic">No fields yet. Add fields manually above.</p>
              )}
            </div>
          </>
        )}

        {/* Location Selection (optional in Step 1) */}
        <div className="border border-gray-200 rounded-lg p-4">
          <label className="flex items-center gap-2 mb-3">
            <input
              type="checkbox"
              checked={wizardData.isLocationSpecific}
              onChange={(e) => handleLocationSpecificToggle(e.target.checked)}
              className="w-4 h-4 border-gray-300 rounded text-purple-600 focus:ring-purple-500"
            />
            <span className="text-sm font-medium text-gray-700">
              Available only to specific locations (optional)
            </span>
          </label>

          {wizardData.isLocationSpecific && (
            <div className="mt-3 space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search locations..."
                  value={locationSearchTerm}
                  onChange={(e) => setLocationSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                />
              </div>

              <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-md p-2">
                {filteredLocations.length === 0 ? (
                  <p className="text-sm text-gray-500 py-2 text-center">No locations found</p>
                ) : (
                  filteredLocations.map((location) => (
                    <label
                      key={location.id}
                      className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={(wizardData.locationIds || []).includes(location.id || '')}
                        onChange={() => handleLocationToggle(location.id || '')}
                        className="w-4 h-4 border-gray-300 rounded text-purple-600 focus:ring-purple-500"
                      />
                      <span className="text-sm text-gray-700">{location.name}</span>
                    </label>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderTemplateStep = () => {
    // If creating template, return null (ComplianceForm rendered separately)
    if (isCreatingTemplate) {
      return null;
    }

    return (
      <div className="space-y-6 p-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Link Compliance</h3>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-2">
            <p className="text-sm text-blue-800">
              <strong>Why this is important:</strong> Linking a compliance template to your form ensures your company has a policy in place to periodically review form completion and data collection. This helps maintain accountability, track progress over time, and ensures compliance requirements are consistently met through structured data capture.
            </p>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Select Compliance Template <span className="text-red-500">*</span>
            </label>
            <button
              type="button"
              onClick={() => setIsCreatingTemplate(true)}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
            >
              <Plus className="w-4 h-4" />
              Add Compliance
            </button>
          </div>

          {/* Search Bar */}
          <div className="mb-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={templateSearchTerm}
                onChange={(e) => setTemplateSearchTerm(e.target.value)}
                placeholder="Search compliance templates..."
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>

          {isLoadingTemplates ? (
            <div className="border border-gray-300 rounded-lg p-4 text-center text-sm text-gray-500">
              Loading templates...
            </div>
          ) : templates.length === 0 ? (
            <div className="border border-gray-300 rounded-lg p-4 text-center">
              <p className="text-sm text-gray-600 mb-3">No templates found. Create one to continue.</p>
              <button
                type="button"
                onClick={() => setIsCreatingTemplate(true)}
                className="btn-primary px-4 py-2 text-sm"
              >
                Create Your First Template
              </button>
            </div>
          ) : filteredTemplates.length === 0 ? (
            <div className="border border-gray-300 rounded-lg p-4 text-center text-sm text-gray-500">
              No templates match your search.
            </div>
          ) : (
            <div className="border border-gray-300 rounded-lg max-h-96 overflow-y-auto">
              {filteredTemplates.map((template) => {
                    const isSelected = wizardData.templateId === template.id;
                    return (
                      <button
                        key={template.id}
                        type="button"
                        onClick={() => template.id && handleTemplateSelect(template.id)}
                        className={`w-full text-left p-4 border-b border-gray-200 last:border-b-0 hover:bg-gray-50 transition-colors ${
                          isSelected ? 'bg-primary-50 border-primary-200' : ''
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              {isSelected && <CheckCircle2 className="w-5 h-5 text-primary-600 flex-shrink-0" />}
                              <h4 className={`font-medium ${isSelected ? 'text-primary-900' : 'text-gray-900'}`}>
                                {template.name}
                              </h4>
                            </div>
                            {template.category && (
                              <p className="text-xs text-gray-500 mt-1">Category: {template.category}</p>
                            )}
                            {template.frequency && (
                              <p className="text-xs text-gray-500">Frequency: {template.frequency}</p>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
          )}

          {wizardData.template && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                <span className="font-medium text-green-900">Compliance Selected</span>
              </div>
              <p className="text-sm text-green-700">{wizardData.template.name}</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const handleReferenceDocumentUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploadingReference(true);
    try {
      const uploadPromises = Array.from(files).map(async (file) => {
        try {
          // Get owner_role from the associated compliance template if available
          let ownerRole: string | undefined;
          if (wizardData.templateId) {
            const template = templates.find(t => t.id === wizardData.templateId);
            ownerRole = template?.owner_role;
          }
          
          const referenceDoc = await uploadReferenceDocument(file, {
            templateId: wizardData.templateId || undefined,
            organizationId: currentOrganization?.id,
            uploadedBy: user
              ? { id: user.id, email: user.email || '', name: user.name || '' }
              : undefined,
            title: file.name,
            source: 'web',
            additionalMetadata: ownerRole ? { owner_role: ownerRole } : undefined,
          });
          return referenceDoc;
        } catch (error: any) {
          console.error(`Error uploading ${file.name}:`, error);
          showNotification(`Failed to upload ${file.name}: ${error.message}`, 'error');
          return null;
        }
      });

      const uploadedDocs = (await Promise.all(uploadPromises)).filter(doc => doc !== null) as ReferenceDocument[];
      
      if (uploadedDocs.length > 0) {
        setWizardData({
          ...wizardData,
          referenceDocuments: [...wizardData.referenceDocuments, ...uploadedDocs]
        });
        showNotification(`Successfully uploaded ${uploadedDocs.length} document(s)`, 'success');
      }
    } catch (error) {
      console.error('Error uploading reference documents:', error);
      showNotification('Error uploading reference documents', 'error');
    } finally {
      setUploadingReference(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const removeReferenceDocument = (index: number) => {
    setWizardData({
      ...wizardData,
      referenceDocuments: wizardData.referenceDocuments.filter((_, i) => i !== index)
    });
  };

  const handleDragOver = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(true);
  };

  const handleDragEnter = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
  };

  const handleDrop = async (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);

    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;

    // Create a fake event object compatible with handleReferenceDocumentUpload
    const fakeEvent = {
      target: {
        files: files,
        value: ''
      }
    } as React.ChangeEvent<HTMLInputElement>;

    await handleReferenceDocumentUpload(fakeEvent);
  };

  const renderLocationsStep = () => {
    return (
      <div className="space-y-6 p-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Reference Documents (Optional)</h3>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-2">
            <p className="text-sm text-blue-800">
              <strong>Why this is important:</strong> Reference documents provide context and guidance for form users. They can include PDFs, images, or other files that help users understand how to fill out the form correctly, provide examples, or reference relevant policies and procedures. These documents are accessible to users when they fill out the form, ensuring consistent and accurate data collection.
            </p>
          </div>
        </div>

        <div className="border border-gray-200 rounded-lg p-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Upload Reference Documents
          </label>
          <label
            className={`flex items-center justify-center w-full px-4 py-6 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
              isDraggingOver
                ? 'border-primary-500 bg-primary-50'
                : 'border-gray-300 hover:border-primary-500 hover:bg-gray-50'
            }`}
            onDragOver={handleDragOver}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div className="flex flex-col items-center">
              {uploadingReference ? (
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mb-2"></div>
              ) : (
                <Upload className="w-6 h-6 text-gray-500 mb-2" />
              )}
              <span className="text-sm text-gray-600">
                {uploadingReference ? 'Uploading...' : isDraggingOver ? 'Drop files here' : 'Upload PDF/Images or drag and drop'}
              </span>
              <span className="text-xs text-gray-500 mt-1">Supports PDF and image files</span>
            </div>
            <input
              type="file"
              className="hidden"
              accept=".pdf,image/*"
              multiple
              onChange={handleReferenceDocumentUpload}
              disabled={uploadingReference}
            />
          </label>
          
          {wizardData.referenceDocuments && wizardData.referenceDocuments.length > 0 && (
            <div className="space-y-2 mt-4 max-h-60 overflow-y-auto">
              {wizardData.referenceDocuments.map((doc, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {doc.type === 'pdf' ? (
                      <FileText className="w-4 h-4 text-red-600 flex-shrink-0" />
                    ) : (
                      <Image className="w-4 h-4 text-green-600 flex-shrink-0" />
                    )}
                    <a
                      href={doc.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-medium text-blue-700 hover:text-blue-900 truncate"
                      title={doc.name}
                    >
                      {doc.name}
                    </a>
                    <span className="text-xs text-gray-500 flex-shrink-0">({doc.type.toUpperCase()})</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeReferenceDocument(index)}
                    className="text-red-600 hover:text-red-800 p-1 flex-shrink-0"
                    title="Remove"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      {/* ComplianceForm as right panel when creating template */}
      {isCreatingTemplate && currentStep === 'template' && (
        <ComplianceForm
          template={undefined}
          onSubmit={handleTemplateCreated}
          onCancel={() => setIsCreatingTemplate(false)}
          categories={categories.length > 0 ? categories : ['Fire Safety', 'Life Safety', 'Health & Safety', 'Equipment Maintenance', 'Building Maintenance', 'Environmental', 'Security', 'Other']}
          zIndex={210}
        />
      )}
      
      <div className="fixed inset-0 bg-black bg-opacity-50 z-[200] flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-2xl max-w-5xl w-full max-h-[95vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Quick Setup: Create Form</h2>
              <p className="text-sm text-gray-600 mt-1">Follow the steps below to create your form with all related items</p>
            </div>
            <button
              onClick={onCancel}
              className="text-gray-500 hover:text-gray-700 p-1"
              title="Close"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Progress Indicator */}
          {renderProgressIndicator()}

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {currentStep === 'form' && renderFormStep()}
            {currentStep === 'template' && renderTemplateStep()}
            {currentStep === 'locations' && renderLocationsStep()}
          </div>

          {/* Footer - Only show when not creating template */}
          {!isCreatingTemplate && (
            <div className="border-t border-gray-200 bg-gray-50 p-6 flex justify-between gap-3">
              <button
                type="button"
                onClick={isFirstStep ? onCancel : handleBack}
                className="btn-secondary px-6 py-2 flex items-center gap-2"
                disabled={isSaving}
              >
                <ChevronLeft className="w-4 h-4" />
                {isFirstStep ? 'Cancel' : 'Back'}
              </button>
              <button
                type="button"
                onClick={handleNext}
                className="btn-primary px-6 py-2 flex items-center gap-2"
                disabled={isSaving}
              >
                {isSaving ? (
                  <>Saving...</>
                ) : isLastStep ? (
                  <>
                    Complete Setup
                    <CheckCircle2 className="w-4 h-4" />
                  </>
                ) : (
                  <>
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Formula Examples Modal */}
      {typeof showFormulaExamples === 'number' && (
        <FormulaExamplesModal
          isOpen={true}
          onClose={() => setShowFormulaExamples(null)}
          availableFields={wizardData.form.fields
            ?.filter(f => f.label && f.type !== 'calculated' && f.order < (wizardData.form.fields[showFormulaExamples]?.order || 0))
            .map(f => ({ label: f.label, type: f.type })) || []}
        />
      )}

      {/* Formula Test Modal */}
      {typeof showFormulaTest === 'number' && (
        <FormulaTestModal
          isOpen={true}
          onClose={() => setShowFormulaTest(null)}
          formula={wizardData.form.fields[showFormulaTest]?.formula || ''}
          fields={wizardData.form.fields || []}
          fieldIndex={showFormulaTest}
        />
      )}

      {/* Table Field Formula Examples Modal */}
      {typeof showFormulaExamples === 'string' && showFormulaExamples.startsWith('table-') && (
        (() => {
          const tableIndex = parseInt(showFormulaExamples.replace('table-', ''));
          const tableField = wizardData.form.tableFields?.[tableIndex];
          if (!tableField) return null;
          return (
            <FormulaExamplesModal
              isOpen={true}
              onClose={() => setShowFormulaExamples(null)}
              availableFields={wizardData.form.tableFields
                ?.filter(f => f.label && f.type !== 'calculated' && f.order < (tableField.order || 0))
                .map(f => ({ label: f.label, type: f.type })) || []}
            />
          );
        })()
      )}
      
      {/* Table Field Formula Test Modal */}
      {typeof showFormulaTest === 'string' && showFormulaTest.startsWith('table-') && (
        (() => {
          const tableIndex = parseInt(showFormulaTest.replace('table-', ''));
          const tableField = wizardData.form.tableFields?.[tableIndex];
          if (!tableField) return null;
          return (
            <FormulaTestModal
              isOpen={true}
              onClose={() => setShowFormulaTest(null)}
              formula={tableField.formula || ''}
              fields={wizardData.form.tableFields || []}
              fieldIndex={tableIndex}
            />
          );
        })()
      )}
    </>
  );
};

export default FormQuickSetupWizard;

